import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import {
  Code,
  Smartphone,
  Cloud,
  Shield,
  Video,
  Headphones,
  ArrowRight,
  MessageSquare,
  FileCode,
  Braces,
  Blocks,
  Database,
  Cpu,
  Server,
  Boxes,
  Cable,
  Terminal,
  Bug,
  Network,
  Lock,
  Eye,
  Router,
  HardDrive
} from "lucide-react";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string; size?: number }>> = {
  code: Code,
  smartphone: Smartphone,
  cloud: Cloud,
  shield: Shield,
  videocam: Video,
  support_agent: Headphones,
  arrow_forward: ArrowRight,
  chat: MessageSquare,
  html: FileCode,
  data_object: Braces,
  code_blocks: Blocks,
  database: Database,
  flutter: Smartphone,
  logo_dev: Cpu,
  android: Cpu,
  phone_iphone: Smartphone,
  cloud_circle: Cloud,
  dns: Server,
  deployed_code: Boxes,
  settings_ethernet: Cable,
  terminal: Terminal,
  bug_report: Bug,
  network_check: Network,
  lock: Lock,
  visibility: Eye,
  router: Router,
  storage: HardDrive,
};

const ServiceIcon: React.FC<{ name: string; className?: string; size?: number }> = ({ name, className = "", size }) => {
  const IconComponent = ICON_MAP[name];
  if (!IconComponent) return null;

  // Sizing mapping
  let defaultSize = 20;
  if (className.includes("text-[24px]")) defaultSize = 24;
  else if (className.includes("text-[20px]")) defaultSize = 20;
  else if (className.includes("text-[18px]")) defaultSize = 18;
  else if (className.includes("text-[16px]")) defaultSize = 16;
  else if (className.includes("text-[14px]")) defaultSize = 14;

  return <IconComponent className={className} size={size || defaultSize} />;
};

const ServicesScreen: React.FC = () => {
  const [openFaq, setOpenFaq] = React.useState<number | null>(null);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="relative min-h-screen pt-12 pb-20 flex justify-center">
      <SEO
        title="Services | InnovaTech"
        description="Partner with Nigeria's premier technology consultancy. InnovaTech delivers enterprise custom software, cloud engineering, cybersecurity defense, and institutional IT infrastructure."
        keywords="top IT consulting agency, best tech partner, best tech partner in nigeria, it consulting agency in jigawa state, software development company nigeria, tech partner for startups nigeria, enterprise it consultancy dutse, cctv and cybersecurity nigeria, hire software developers nigeria"
        canonical="/services"
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "InnovaTech Consultancy Limited",
            alternateName: "InnovaTech",
            url: "https://www.innovatech-ng.com/services",
            logo: "https://www.innovatech-ng.com/logo/logo.png",
            telephone: "+234 813 023 7608",
            priceRange: "₦₦₦",
            address: {
              "@type": "PostalAddress",
              streetAddress: "No.2 Salmaniyya Plaza, Opp. TCN, Ibrahim Aliyu Bypass",
              addressLocality: "Dutse",
              addressRegion: "Jigawa",
              postalCode: "720101",
              addressCountry: "NG",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 11.6928,
              longitude: 9.348,
            },
            serviceType: [
              "IT Consulting",
              "Custom Software Development",
              "Enterprise Web Development",
              "Mobile Application Development",
              "Cloud Infrastructure",
              "Cybersecurity",
              "CCTV & Surveillance Installation",
              "VoIP Systems",
              "Data Analytics",
            ],
            areaServed: [
              { "@type": "State", name: "Jigawa State" },
              { "@type": "State", name: "Kano State" },
              { "@type": "Country", name: "Nigeria" },
            ],
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "InnovaTech IT Consulting & Engineering Services",
              itemListElement: [
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Enterprise Web & Software Development",
                    description:
                      "Custom web application and software development using React, Node.js, and TypeScript for businesses and government agencies.",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "IT Consulting & Digital Transformation",
                    description:
                      "Strategic IT consulting, system architecture design, digital workflow automation, and enterprise technology advisory.",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Cybersecurity & Infrastructure Hardening",
                    description:
                      "Vulnerability assessments, penetration testing, AES-256 encrypted network architectures, and security audits.",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "CCTV Surveillance & Physical Security",
                    description:
                      "Professional IP camera installation, NVR configuration, and AI-enabled surveillance setups for institutions and commercial facilities.",
                  },
                },
              ],
            },
          })}
        </script>

        {/* FAQPage Schema for IT Consulting & Tech Partner Queries */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Why is InnovaTech ranked among the top IT consulting agencies in Nigeria and Jigawa State?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "InnovaTech combines over 15 years of industry experience with 500+ successful projects delivered across Nigeria. We deliver full-cycle IT consulting from system architecture, cloud migration, and cybersecurity audits to custom enterprise software.",
                },
              },
              {
                "@type": "Question",
                name: "How does InnovaTech act as a technology partner for startups and businesses?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "As a dedicated tech partner, InnovaTech provides end-to-end technical execution: product discovery, UI/UX design, custom software engineering, scalable cloud infrastructure, and 24/7 maintenance with guaranteed SLAs.",
                },
              },
              {
                "@type": "Question",
                name: "Do you provide on-site engineering and CCTV installation in Northern Nigeria?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes, our certified physical engineering team handles LAN cabling, server racks, high-density Wi-Fi, and IP CCTV surveillance across Jigawa, Kano, Abuja, and all Northern Nigerian states.",
                },
              },
            ],
          })}
        </script>
      </SEO>
      <div className="w-full max-w-[1200px] px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-12">
        <aside className="hidden lg:block relative">
          <div className="sticky top-28 flex flex-col gap-8">
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-text-sub dark:text-gray-400 mb-4 pl-3">
                Service Catalog
              </h2>
              <nav
                aria-label="Services navigation"
                className="flex flex-col gap-1"
              >
                <a
                  href="#web-dev"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("web-dev");
                  }}
                  className="group flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white dark:bg-white/5 border border-border-subtle dark:border-white/10 shadow-sm transition-all hover:border-primary/30 w-full text-left"
                >
                  <ServiceIcon name="code" className="text-primary text-[20px]" />
                  <span className="text-text-main dark:text-white text-sm font-medium">
                    Web Development
                  </span>
                </a>
                <a
                  href="#mobile-app"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("mobile-app");
                  }}
                  className="group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all hover:bg-white dark:hover:bg-white/5 hover:shadow-sm text-text-sub dark:text-gray-400 hover:text-text-main dark:hover:text-white border border-transparent hover:border-border-subtle dark:hover:border-white/10 w-full text-left"
                >
                  <ServiceIcon name="smartphone" className="text-[20px] group-hover:text-primary/70 transition-colors" />
                  <span className="text-sm font-medium">
                    Mobile Application
                  </span>
                </a>
                <a
                  href="#cloud-infra"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("cloud-infra");
                  }}
                  className="group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all hover:bg-white dark:hover:bg-white/5 hover:shadow-sm text-text-sub dark:text-gray-400 hover:text-text-main dark:hover:text-white border border-transparent hover:border-border-subtle dark:hover:border-white/10 w-full text-left"
                >
                  <ServiceIcon name="cloud" className="text-[20px] group-hover:text-primary/70 transition-colors" />
                  <span className="text-sm font-medium">
                    Cloud Infrastructure
                  </span>
                </a>
                <a
                  href="#cybersecurity"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("cybersecurity");
                  }}
                  className="group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all hover:bg-white dark:hover:bg-white/5 hover:shadow-sm text-text-sub dark:text-gray-400 hover:text-text-main dark:hover:text-white border border-transparent hover:border-border-subtle dark:hover:border-white/10 w-full text-left"
                >
                  <ServiceIcon name="shield" className="text-[20px] group-hover:text-primary/70 transition-colors" />
                  <span className="text-sm font-medium">Cybersecurity</span>
                </a>
                <a
                  href="#cctv"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("cctv");
                  }}
                  className="group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all hover:bg-white dark:hover:bg-white/5 hover:shadow-sm text-text-sub dark:text-gray-400 hover:text-text-main dark:hover:text-white border border-transparent hover:border-border-subtle dark:hover:border-white/10 w-full text-left"
                >
                  <ServiceIcon name="videocam" className="text-[20px] group-hover:text-primary/70 transition-colors" />
                  <span className="text-sm font-medium">
                    CCTV & Surveillance
                  </span>
                </a>
              </nav>
            </div>
            <div className="p-4 rounded-xl bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 shadow-sm flex flex-col gap-3">
              <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-1">
                <ServiceIcon name="support_agent"  />
              </div>
              <h3 className="text-sm font-semibold text-text-main dark:text-white">
                Need a Consultation?
              </h3>
              <p className="text-xs text-text-sub dark:text-gray-400 leading-relaxed">
                Not sure which solution fits your business? Talk to our
                engineers for a free assessment.
              </p>
              <Link
                to="/contact"
                className="text-primary text-xs font-semibold text-left hover:underline flex items-center gap-1"
              >
                Book a call{" "}
                <ServiceIcon name="arrow_forward" className="text-[14px]" />
              </Link>
            </div>
          </div>
        </aside>

        <main className="flex flex-col gap-8 min-w-0">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
                00 · ENTERPRISE IT &amp; ENGINEERING
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-text-main dark:text-white mb-3">
              Top IT Consulting Agency &amp; Software Tech Partner
            </h1>
            <p className="text-text-sub dark:text-gray-400 max-w-2xl leading-relaxed text-sm sm:text-base">
              InnovaTech is Nigeria's trusted technology partner and IT consultancy. We architect and build custom software, deploy hardened cybersecurity defenses, design enterprise networks, and install commercial CCTV surveillance for startups, established businesses, and government institutions in Jigawa State and nationwide.
            </p>
          </div>

          {/* Web Dev Section */}
          <section
            id="web-dev"
            className="bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow duration-300"
          >
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-6">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-1.5 rounded bg-primary/10 text-primary">
                    <ServiceIcon name="code" className="text-[24px]" />
                  </div>
                  <h2 className="text-xl font-semibold text-text-main dark:text-white">
                    Enterprise Web Development
                  </h2>
                </div>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed max-w-xl mb-4">
                  We design and develop high-performance web applications for
                  businesses of all sizes — from early-stage startups to large
                  enterprises. Our full-cycle development service covers
                  everything from UX design and frontend engineering to backend
                  API development, database architecture, and production
                  deployment. We build with React, Next.js, Node.js, and
                  TypeScript to deliver fast, accessible, and maintainable
                  products that scale with your growth.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {[
                    "Custom Online Platforms & Portals",
                    "Online Shopping & E-commerce Stores",
                    "Works on All Devices (Phone, Tablet, PC)",
                    "Connects Your Website to Other Apps",
                    "Admin Dashboards & Content Management",
                    "Built to Rank Well on Google",
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-center gap-2 text-sm text-text-sub dark:text-gray-400"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/contact"
                className="shrink-0 bg-white dark:bg-transparent border border-border-subtle dark:border-white/20 hover:border-primary text-text-main dark:text-white hover:text-primary px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center gap-2 group"
              >
                <ServiceIcon name="chat" className="text-[18px]" />
                Talk to Us
              </Link>
            </div>
            <div className="mb-6">
              <span className="text-xs font-semibold text-text-sub dark:text-gray-400 uppercase tracking-wider mb-3 block">
                Tech Stack
              </span>
              <div className="flex flex-wrap gap-2">
                {[
                  { icon: "html", name: "React (Web Framework)" },
                  { icon: "data_object", name: "Node.js (Server)" },
                  { icon: "code_blocks", name: "TypeScript (Safe Code)" },
                  { icon: "database", name: "PostgreSQL (Database)" },
                ].map((tech, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-1.5 bg-canvas dark:bg-canvas-dark border border-border-subtle dark:border-white/10 rounded-md"
                  >
                    <ServiceIcon name={tech.icon} className="text-[16px] text-text-main dark:text-white" />
                    <span className="text-xs font-mono text-text-main dark:text-white">
                      {tech.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-canvas dark:bg-canvas-dark border border-border-subtle dark:border-white/10 rounded-xl p-6">
              <span className="text-xs font-semibold text-text-sub dark:text-gray-400 uppercase tracking-wider mb-4 block">
                Development Process
              </span>
              <div className="flex flex-col sm:flex-row justify-between relative gap-6 sm:gap-0">
                <div className="hidden sm:block absolute top-4 left-4 right-4 h-[2px] bg-border-subtle dark:bg-white/10 -z-10"></div>
                {[
                  "Discovery",
                  "Planning",
                  "Development",
                  "Testing",
                  "Deploy",
                ].map((step, i) => (
                  <div
                    key={i}
                    className={`flex sm:flex-col items-center gap-4 sm:gap-2 ${i === 0 ? "sm:items-start" : i === 4 ? "sm:items-end" : "sm:items-center"}`}
                  >
                    <div
                      className={`size-8 rounded-full border-2 flex items-center justify-center text-xs font-bold z-10 ${i === 0 ? "bg-surface dark:bg-surface-dark border-primary text-primary" : "bg-surface dark:bg-surface-dark border-text-sub dark:border-gray-500 text-text-sub dark:text-gray-400"}`}
                    >
                      {i + 1}
                    </div>
                    <div
                      className={`text-left sm:text-center sm:w-20 ${i === 4 ? "sm:text-right" : ""}`}
                    >
                      <p
                        className={`text-xs font-semibold ${i === 0 ? "text-text-main dark:text-white" : "text-text-sub dark:text-gray-400"}`}
                      >
                        {step}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Mobile App Section */}
          <section
            id="mobile-app"
            className="bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow duration-300"
          >
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-6">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-1.5 rounded bg-primary/10 text-primary">
                    <ServiceIcon name="smartphone" className="text-[24px]" />
                  </div>
                  <h2 className="text-xl font-semibold text-text-main dark:text-white">
                    Mobile Application Development
                  </h2>
                </div>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed max-w-xl mb-4">
                  We build cross-platform and native mobile applications for iOS
                  and Android that are fast, intuitive, and built for scale.
                  Whether you need a consumer-facing app, a business operations
                  tool, or a real-time data platform, our mobile engineers
                  deliver polished products using Flutter, React Native, Swift,
                  and Kotlin. We focus on offline-first architecture, battery
                  efficiency, and smooth UX across all screen sizes.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {[
                    "iPhone & Android Apps",
                    "One App That Works on All Phones",
                    "Works Without Internet Connection",
                    "Instant Alerts & Live Updates",
                    "Published to App Store & Play Store",
                    "Fast & Battery-Friendly",
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-center gap-2 text-sm text-text-sub dark:text-gray-400"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/contact"
                className="shrink-0 bg-white dark:bg-transparent border border-border-subtle dark:border-white/20 hover:border-primary text-text-main dark:text-white hover:text-primary px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center gap-2 group"
              >
                <ServiceIcon name="chat" className="text-[18px]" />
                Talk to Us
              </Link>
            </div>
            <div className="mb-6">
              <span className="text-xs font-semibold text-text-sub dark:text-gray-400 uppercase tracking-wider mb-3 block">
                Tech Stack
              </span>
              <div className="flex flex-wrap gap-2">
                {[
                  { icon: "flutter", name: "Flutter (Cross-Platform)" },
                  { icon: "logo_dev", name: "React Native (Cross-Platform)" },
                  { icon: "android", name: "Kotlin (Android)" },
                  { icon: "phone_iphone", name: "Swift (iPhone)" },
                ].map((tech, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-1.5 bg-canvas dark:bg-canvas-dark border border-border-subtle dark:border-white/10 rounded-md"
                  >
                    <ServiceIcon name={tech.icon} className="text-[16px] text-text-main dark:text-white" />
                    <span className="text-xs font-mono text-text-main dark:text-white">
                      {tech.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Cloud Infra Section */}
          <section
            id="cloud-infra"
            className="bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow duration-300"
          >
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-6">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-1.5 rounded bg-primary/10 text-primary">
                    <ServiceIcon name="cloud" className="text-[24px]" />
                  </div>
                  <h2 className="text-xl font-semibold text-text-main dark:text-white">
                    Cloud Infrastructure & DevOps
                  </h2>
                </div>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed max-w-xl mb-4">
                  We design, deploy, and manage resilient cloud infrastructure
                  on AWS and Azure for organisations that need high
                  availability, security, and cost efficiency. Our DevOps
                  engineers handle everything from initial cloud architecture
                  assessment and server migration to CI/CD pipeline setup,
                  container orchestration with Kubernetes, and ongoing
                  infrastructure monitoring. We help teams move faster with
                  automated deployments and reduced operational overhead.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {[
                    "Moving Your Business to the Cloud",
                    "Online Storage & Server Setup",
                    "Keeps Your System Running Without Crashing",
                    "Automated Updates & Faster Deployments",
                    "Pay-as-You-Use Computing",
                    "Round-the-Clock System Monitoring",
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-center gap-2 text-sm text-text-sub dark:text-gray-400"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/contact"
                className="shrink-0 bg-white dark:bg-transparent border border-border-subtle dark:border-white/20 hover:border-primary text-text-main dark:text-white hover:text-primary px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center gap-2 group"
              >
                <ServiceIcon name="chat" className="text-[18px]" />
                Talk to Us
              </Link>
            </div>
            <div className="mb-6">
              <span className="text-xs font-semibold text-text-sub dark:text-gray-400 uppercase tracking-wider mb-3 block">
                Tech Stack
              </span>
              <div className="flex flex-wrap gap-2">
                {[
                  { icon: "cloud_circle", name: "Amazon Cloud (AWS)" },
                  { icon: "dns", name: "Microsoft Cloud (Azure)" },
                  { icon: "deployed_code", name: "Auto-Scaling Containers" },
                  { icon: "settings_ethernet", name: "Infrastructure Automation" },
                ].map((tech, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-1.5 bg-canvas dark:bg-canvas-dark border border-border-subtle dark:border-white/10 rounded-md"
                  >
                    <ServiceIcon name={tech.icon} className="text-[16px] text-text-main dark:text-white" />
                    <span className="text-xs font-mono text-text-main dark:text-white">
                      {tech.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-canvas dark:bg-canvas-dark border border-border-subtle dark:border-white/10 rounded-xl p-6">
              <span className="text-xs font-semibold text-text-sub dark:text-gray-400 uppercase tracking-wider mb-4 block">
                Migration Process
              </span>
              <div className="flex flex-col sm:flex-row justify-between relative gap-6 sm:gap-0">
                <div className="hidden sm:block absolute top-4 left-4 right-4 h-[2px] bg-border-subtle dark:bg-white/10 -z-10"></div>
                {[
                  "Audit",
                  "Strategy",
                  "Migration",
                  "Optimisation",
                  "Support",
                ].map((step, i) => (
                  <div
                    key={i}
                    className={`flex sm:flex-col items-center gap-4 sm:gap-2 ${i === 0 ? "sm:items-start" : i === 4 ? "sm:items-end" : "sm:items-center"}`}
                  >
                    <div className="size-8 rounded-full border-2 flex items-center justify-center text-xs font-bold z-10 bg-surface dark:bg-surface-dark border-text-sub dark:border-gray-500 text-text-sub dark:text-gray-400">
                      {i + 1}
                    </div>
                    <div
                      className={`text-left sm:text-center sm:w-20 ${i === 4 ? "sm:text-right" : ""}`}
                    >
                      <p className="text-xs font-semibold text-text-sub dark:text-gray-400">
                        {step}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Cybersecurity Section */}
          <section
            id="cybersecurity"
            className="bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow duration-300"
          >
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-6">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-1.5 rounded bg-primary/10 text-primary">
                    <ServiceIcon name="shield" className="text-[24px]" />
                  </div>
                  <h2 className="text-xl font-semibold text-text-main dark:text-white">
                    Cybersecurity Services
                  </h2>
                </div>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed max-w-xl mb-4">
                  We help organisations protect their digital assets, systems,
                  and data from cyber threats through proactive security
                  assessments, penetration testing, and continuous monitoring.
                  Our cybersecurity team uses industry-standard tools and
                  methodologies to identify vulnerabilities before attackers do.
                  From firewall configuration and AES-256 encryption to
                  compliance auditing and incident response planning, we provide
                  comprehensive protection for businesses of all sizes.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {[
                    "Hacking Risk Checks (Pen Testing)",
                    "Find & Fix Security Weaknesses",
                    "Lock Down Your Data with Encryption",
                    "Meet Legal & Regulatory Standards",
                    "What To Do If You Get Hacked",
                    "24/7 Threat Watch & Alerts",
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-center gap-2 text-sm text-text-sub dark:text-gray-400"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/contact"
                className="shrink-0 bg-white dark:bg-transparent border border-border-subtle dark:border-white/20 hover:border-primary text-text-main dark:text-white hover:text-primary px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center gap-2 group"
              >
                <ServiceIcon name="chat" className="text-[18px]" />
                Talk to Us
              </Link>
            </div>
            <div className="mb-6">
              <span className="text-xs font-semibold text-text-sub dark:text-gray-400 uppercase tracking-wider mb-3 block">
                Security Tools
              </span>
              <div className="flex flex-wrap gap-2">
                {[
                  { icon: "terminal", name: "Security Testing Tools" },
                  { icon: "bug_report", name: "Attack Simulation" },
                  { icon: "network_check", name: "Network Traffic Analysis" },
                  { icon: "lock", name: "Data Encryption" },
                ].map((tech, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-1.5 bg-canvas dark:bg-canvas-dark border border-border-subtle dark:border-white/10 rounded-md"
                  >
                    <ServiceIcon name={tech.icon} className="text-[16px] text-text-main dark:text-white" />
                    <span className="text-xs font-mono text-text-main dark:text-white">
                      {tech.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CCTV Section */}
          <section
            id="cctv"
            className="bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow duration-300"
          >
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-6">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-1.5 rounded bg-primary/10 text-primary">
                    <ServiceIcon name="videocam" className="text-[24px]" />
                  </div>
                  <h2 className="text-xl font-semibold text-text-main dark:text-white">
                    CCTV &amp; Surveillance Installation
                  </h2>
                </div>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed max-w-xl mb-4">
                  We provide professional CCTV and IP surveillance system
                  installation for homes, offices, warehouses, schools, and
                  large commercial facilities. Our team handles everything from
                  site surveys and system design to hardware installation, NVR
                  configuration, and remote access setup. We work with leading
                  brands including Hikvision, Axis, and Cisco to deliver HD
                  camera systems with encrypted remote viewing, motion detection
                  alerts, and long-term storage solutions.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {[
                    "Indoor & Outdoor Camera Fitting",
                    "Video Recorder Setup (DVR/NVR)",
                    "Watch Your Property from Your Phone",
                    "Automatic Alerts When Movement Detected",
                    "Door & Gate Access Control",
                    "Regular Checks & Repairs",
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-center gap-2 text-sm text-text-sub dark:text-gray-400"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/contact"
                className="shrink-0 bg-primary border border-primary text-white hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-bold transition-colors shadow-lg shadow-primary/20 flex items-center gap-2 group"
              >
                <ServiceIcon name="chat" className="text-[18px]" />
                Talk to Us
              </Link>
            </div>
            <div className="mb-6">
              <span className="text-xs font-semibold text-text-sub dark:text-gray-400 uppercase tracking-wider mb-3 block">
                Hardware &amp; Systems
              </span>
              <div className="flex flex-wrap gap-2">
                {[
                  { icon: "videocam", name: "Hikvision Cameras" },
                  { icon: "visibility", name: "Axis Cameras" },
                  { icon: "router", name: "Cisco Networking" },
                  { icon: "storage", name: "Digital Video Recorder" },
                ].map((tech, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-1.5 bg-canvas dark:bg-canvas-dark border border-border-subtle dark:border-white/10 rounded-md"
                  >
                    <ServiceIcon name={tech.icon} className="text-[16px] text-text-main dark:text-white" />
                    <span className="text-xs font-mono text-text-main dark:text-white">
                      {tech.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-canvas dark:bg-canvas-dark border border-border-subtle dark:border-white/10 rounded-xl p-6">
              <span className="text-xs font-semibold text-text-sub dark:text-gray-400 uppercase tracking-wider mb-4 block">
                Installation Process
              </span>
              <div className="flex flex-col sm:flex-row justify-between relative gap-6 sm:gap-0">
                <div className="hidden sm:block absolute top-4 left-4 right-4 h-[2px] bg-border-subtle dark:bg-white/10 -z-10"></div>
                {[
                  "Site Survey",
                  "System Design",
                  "Installation",
                  "Configuration",
                  "Handover",
                ].map((step, i) => (
                  <div
                    key={i}
                    className={`flex sm:flex-col items-center gap-4 sm:gap-2 ${i === 0 ? "sm:items-start" : i === 4 ? "sm:items-end" : "sm:items-center"}`}
                  >
                    <div className="size-8 rounded-full border-2 flex items-center justify-center text-xs font-bold z-10 bg-surface dark:bg-surface-dark border-text-sub dark:border-gray-500 text-text-sub dark:text-gray-400">
                      {i + 1}
                    </div>
                    <div
                      className={`text-left sm:text-center sm:w-20 ${i === 4 ? "sm:text-right" : ""}`}
                    >
                      <p className="text-xs font-semibold text-text-sub dark:text-gray-400">
                        {step}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Minimalist Overview Highlights & FAQ Section for Tech Partner */}
          <section className="reveal mt-28 md:mt-36 flex flex-col gap-24 border-t border-black/[0.06] dark:border-white/10 pt-16">
            {/* Section Header */}
            <div className="flex flex-col gap-5 text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
                  07 · PARTNERSHIP
                </span>
              </div>
              
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.035em] text-text-main dark:text-white leading-[1.05]">
                Why InnovaTech is the #1 Tech Partner for <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-500">
                  Nigerian Businesses.
                </span>
              </h2>
              <p className="text-sm sm:text-base text-text-sub dark:text-gray-400 font-light leading-relaxed max-w-2xl">
                Whether you need dedicated full-stack software engineers, an end-to-end IT modernization plan, or institutional security infrastructure across Nigeria, we deliver proven execution.
              </p>

              {/* Three Cards Matching Site's Nested Glass Card Language */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
                <div className="p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft">
                  <div className="rounded-[1.85rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 p-7 flex flex-col justify-between h-full relative overflow-hidden">
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-mono text-xs font-bold tracking-widest text-primary">01</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-text-sub dark:text-gray-400 bg-black/[0.03] dark:bg-white/5 px-2.5 py-1 rounded-full border border-black/[0.03] dark:border-white/5">
                        Guaranteed
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-text-main dark:text-white mb-2.5">
                        Strict SLAs &amp; Delivery
                      </h3>
                      <p className="text-xs sm:text-sm font-light text-text-sub dark:text-gray-400 leading-relaxed">
                        Milestone-driven project completion, clean documentation, and reliable ongoing support.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft">
                  <div className="rounded-[1.85rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 p-7 flex flex-col justify-between h-full relative overflow-hidden">
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-mono text-xs font-bold tracking-widest text-primary">02</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-text-sub dark:text-gray-400 bg-black/[0.03] dark:bg-white/5 px-2.5 py-1 rounded-full border border-black/[0.03] dark:border-white/5">
                        On-Site
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-text-main dark:text-white mb-2.5">
                        Regional Engineering
                      </h3>
                      <p className="text-xs sm:text-sm font-light text-text-sub dark:text-gray-400 leading-relaxed">
                        Headquartered in Dutse with hands-on physical deployment teams across Northern Nigeria.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft">
                  <div className="rounded-[1.85rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 p-7 flex flex-col justify-between h-full relative overflow-hidden">
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-mono text-xs font-bold tracking-widest text-primary">03</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-text-sub dark:text-gray-400 bg-black/[0.03] dark:bg-white/5 px-2.5 py-1 rounded-full border border-black/[0.03] dark:border-white/5">
                        Security
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-text-main dark:text-white mb-2.5">
                        Bank-Grade Defense
                      </h3>
                      <p className="text-xs sm:text-sm font-light text-text-sub dark:text-gray-400 leading-relaxed">
                        AES-256 encryption, certified vulnerability scanning, and NDPR-compliant data protection.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Minimalist FAQ Accordion Section */}
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-4">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
                    08 · QUESTIONS
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-[-0.03em] text-text-main dark:text-white leading-[1.1]">
                  Frequently Asked <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-500">
                    Questions.
                  </span>
                </h3>
              </div>

              <div className="flex flex-col gap-4">
                {[
                  {
                    q: "Why is InnovaTech ranked among the top IT consulting agencies in Nigeria and Jigawa State?",
                    a: "InnovaTech combines over 15 years of industry experience with 500+ successful projects delivered across Nigeria. We deliver full-cycle IT consulting from system architecture, cloud migration, and cybersecurity audits to custom enterprise software."
                  },
                  {
                    q: "How does InnovaTech act as a technology partner for startups and businesses?",
                    a: "As a dedicated tech partner, InnovaTech provides end-to-end technical execution: product discovery, UI/UX design, custom software engineering, scalable cloud infrastructure, and 24/7 maintenance with guaranteed SLAs."
                  },
                  {
                    q: "Do you provide on-site engineering and CCTV installation in Northern Nigeria?",
                    a: "Yes, our certified physical engineering team handles LAN cabling, server racks, high-density Wi-Fi, and IP CCTV surveillance across Jigawa, Kano, Abuja, and all Northern Nigerian states."
                  }
                ].map((faq, idx) => (
                  <div
                    key={idx}
                    className="p-1 rounded-[1.75rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-soft transition-all duration-300"
                  >
                    <div className="rounded-[1.5rem] bg-white dark:bg-[#0E1F35] border border-black/5 dark:border-white/5 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                        className="w-full p-6 sm:p-7 text-left flex items-center justify-between gap-4 font-semibold text-sm sm:text-base text-text-main dark:text-white cursor-pointer hover:bg-black/[0.01] dark:hover:bg-white/[0.01] transition-colors"
                      >
                        <span className="font-display tracking-tight">{faq.q}</span>
                        <span className="font-mono text-xs font-bold text-primary shrink-0 ml-2">
                          {openFaq === idx ? "−" : "+"}
                        </span>
                      </button>
                      {openFaq === idx && (
                        <div className="px-6 pb-6 sm:px-7 sm:pb-7 pt-1 text-xs sm:text-sm text-text-sub dark:text-gray-300 font-light leading-relaxed border-t border-black/[0.04] dark:border-white/5">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default ServicesScreen;
